"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useRef, useState } from "react";

// Footer scope: only the "outer" root-main pages — never the learning views
// (Teori/Övning live under /kurser with their own scroll container and were
// deliberately kept footer-free). The login page is special-cased to always
// show the footer (see `alwaysVisible`).
const FOOTER_ROUTES = new Set<string>([
  "/",
  "/login",
  "/installningar",
  "/valj-kurser",
  "/glomt-losenord",
  "/aterstall-losenord",
  "/integritetspolicy",
  "/anvandarvillkor",
  "/tillganglighet",
]);

// px slack so sub-pixel rounding / momentum scroll still counts as "at bottom".
const BOTTOM_SLACK = 8;

export default function Footer() {
  const pathname = usePathname();
  const enabled = pathname ? FOOTER_ROUTES.has(pathname) : false;
  const alwaysVisible = pathname === "/login";
  const [atBottom, setAtBottom] = useState(false);
  const observerRef = useRef<ResizeObserver | null>(null);

  useEffect(() => {
    // When the footer is forced visible (login) or hidden (non-footer route)
    // the measured value is unused, so we skip wiring up listeners entirely.
    // The effect re-runs on `pathname`, so a stale value is recomputed on the
    // next footer route via the initial `update()` below.
    if (!enabled || alwaysVisible) return;
    // The root scroll container is the single top-level <main> in RootLayout.
    const main = document.querySelector("main");
    if (!main) return;

    const update = () => {
      const scrollable = main.scrollHeight - main.clientHeight > BOTTOM_SLACK;
      const reachedBottom =
        main.scrollTop + main.clientHeight >= main.scrollHeight - BOTTOM_SLACK;
      // Only reveal once there is something to scroll AND the user reached the
      // end. Short pages that never scroll stay footer-free.
      setAtBottom(scrollable && reachedBottom);
    };

    update();
    main.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    // Content can grow after async data loads (e.g. session fetch) — re-measure
    // when the scroll content changes height.
    const observer = new ResizeObserver(update);
    observer.observe(main);
    if (main.firstElementChild) observer.observe(main.firstElementChild);
    observerRef.current = observer;

    return () => {
      main.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      observer.disconnect();
      observerRef.current = null;
    };
  }, [enabled, alwaysVisible, pathname]);

  if (!enabled) return null;

  const visible = alwaysVisible || atBottom;

  return (
    <footer
      className={`fixed bottom-0 left-0 right-0 z-40 bg-[var(--color-nav-bg)] border-t border-[var(--color-nav-border)] transition-[transform,opacity] duration-300 ease-out ${
        visible
          ? "translate-y-0 opacity-100"
          : "translate-y-full opacity-0 pointer-events-none"
      }`}
    >
      <div className="mx-auto max-w-5xl px-6 min-h-10 py-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1 text-xs text-[var(--color-nav-text-muted)]">
        <span className="text-[var(--color-nav-text)]">© 2026 vektr</span>
        <span aria-hidden="true" className="opacity-40">
          ·
        </span>
        <a
          href="mailto:kontakt@vektr.se"
          title="Skicka e-post till oss på kontakt@vektr.se"
          className="hover:text-[var(--color-accent)] transition-colors"
        >
          Kontakt
        </a>
        <Link
          href="/integritetspolicy"
          title="Läs hur vi behandlar personuppgifter"
          className="hover:text-[var(--color-accent)] transition-colors"
        >
          Integritetspolicy
        </Link>
        <Link
          href="/anvandarvillkor"
          title="Läs villkoren för att använda vektr"
          className="hover:text-[var(--color-accent)] transition-colors"
        >
          Användarvillkor
        </Link>
        <Link
          href="/tillganglighet"
          title="Läs vår tillgänglighetsredogörelse"
          className="hover:text-[var(--color-accent)] transition-colors"
        >
          Tillgänglighetsredogörelse
        </Link>
      </div>
    </footer>
  );
}
